// Shared contract for changing a project's models from a description the user gives AI, such as "add two people to
// Accounts Payable" or "replace Xero with NetSuite". The client sends the project's roles, systems and actions; AI asks
// questions until the description is clear, then returns changes, which the client makes in the project.
// Shared with linq-server (copied by its `npm run copy`), so it must only import from this folder.

import { EPM_CHANGE_KINDS, EpmNextStep } from './epm-decisions';

// the changes AI can make. As well as the scorecard's changes to actions and roles (see EPM_CHANGE_KINDS):
//   replaceSystem: another system, which may be new, does everything a system did, in every model
//   replaceRole: another role, which may be new, does everything a role did, in every model
//   roleCost: a role's annual cost per person
//   systemCost: a system's fixed annual cost
export const MODEL_CHANGE_KINDS = [...EPM_CHANGE_KINDS, 'replaceSystem', 'replaceRole', 'roleCost', 'systemCost'] as const;
export type ModelChangeKind = typeof MODEL_CHANGE_KINDS[number];

// a change as AI returns it. Structured output has no optional fields, so what a kind doesn't use is null
export interface AIModelChange {
    kind: ModelChangeKind;
    action: string | null;          // the action changed, by name
    model: string | null;           // the model the action is in
    role: string | null;            // the role changed, replaced, or giving an action up
    newRole: string | null;         // the role taking an action on, or replacing role. May be new
    system: string | null;          // the system changed or replaced, or automating or assisting an action. May be new
    newSystem: string | null;       // the system replacing system. May be new
    percent: number | null;         // an action's new time as a percentage of what it takes today. share: the share of items, 0 to 100
    people: number | null;          // how many people a role has (staff), or a new role has
    cost: number | null;            // roleCost: per person a year. systemCost: a year
    from: string | null;            // share: the step or information the items leave, by name. add: the step or information the new step follows
    items: number | null;           // volume: items a year
    output: string | null;          // add: the information the new step creates
    minutes: number | null;         // add: the new step's time per item
    description: string;            // the change in a sentence
}

// what AI is told about the project. Roles and systems list the models they're used in; actions, the time each item
// takes and the steps their items go to next, so the flow can be restructured
export interface ModelChangeContext {
    project: string;
    currency: string;
    models: string[];
    roles: { name: string; people: number; annualCost: number; models: string[] }[];
    systems: { name: string; annualCost: number; models: string[] }[];
    actions: { name: string; model: string; roles: string[]; systems: string[]; hours: number; minutes?: number; next?: EpmNextStep[] }[];
}

export interface ModelChangeAnswer {
    question: string;
    answer: string;
}

export interface ModelChangeRequest {
    description: string;
    answers: ModelChangeAnswer[];       // to questions AI asked about the description
    context: ModelChangeContext;
}

// either questions, when the description isn't clear enough to act on, or the changes
export interface AIModelChangeResponse {
    ready: boolean;
    questions: string[];
    summary: string;
    changes: AIModelChange[];
}

// what the server returns: the changes it accepted, and those it didn't with why
export interface ModelChangeResponse extends AIModelChangeResponse {
    rejected: { description: string; reason: string }[];
}

export const MODEL_CHANGE_LIMITS = { description: 2000, answers: 10, answer: 1000, actions: 400, roles: 300, systems: 300, models: 300, changes: 60, people: 1000, percent: 200, cost: 100_000_000, items: 100_000_000, minutes: 100_000 };

const sameName = (a: string | null | undefined, b: string | null | undefined) =>
    !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();

// whether the text names it as words of their own, so a model called "A" isn't found in every sentence
const mentions = (text: string, name: string) => {
    const words = (name || '').trim().toLowerCase();
    if (!words) return false;
    const escaped = words.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp(`(^|[^\\p{L}\\p{N}])${escaped}($|[^\\p{L}\\p{N}])`, 'u').test(text);
};

// keeps the context within bounds. Actions are many, so those the description (or its answers) mentions, by their
// name or their model's, come first, then the heaviest
export const limitModelChangeContext = (context: ModelChangeContext, description: string, answers: ModelChangeAnswer[] = []): ModelChangeContext => {
    const text = [description, ...answers.map((a) => a.answer)].join(' ').toLowerCase();
    const actions = [...(context.actions || [])]
        .map((a) => ({ a, mentioned: mentions(text, a.name) || mentions(text, a.model) }))
        .sort((x, y) => Number(y.mentioned) - Number(x.mentioned) || (y.a.hours || 0) - (x.a.hours || 0))
        .slice(0, MODEL_CHANGE_LIMITS.actions)
        .map((x) => x.a);
    return {
        project: context.project,
        currency: context.currency,
        models: (context.models || []).slice(0, MODEL_CHANGE_LIMITS.models),
        roles: (context.roles || []).slice(0, MODEL_CHANGE_LIMITS.roles),
        systems: (context.systems || []).slice(0, MODEL_CHANGE_LIMITS.systems),
        actions,
    };
};

const validNumber = (value: number | null, max: number, whole = false) =>
    value !== null && Number.isFinite(value) && value >= 0 && value <= max && (!whole || Number.isInteger(value));

// why a change can't be made, or undefined when it can. What it changes must be in the project; a role or system it
// adds may be new
export const checkModelChange = (change: AIModelChange, context: ModelChangeContext): string | undefined => {
    const hasRole = (name: string | null) => (context.roles || []).some((r) => sameName(r.name, name));
    const hasSystem = (name: string | null) => (context.systems || []).some((s) => sameName(s.name, name));

    switch (change.kind) {
        case 'staff':
            if (!hasRole(change.role)) return `the role ${change.role} isn't in the project`;
            return validNumber(change.people, MODEL_CHANGE_LIMITS.people, true) ? undefined : 'it has no valid number of people';
        case 'roleCost':
            if (!hasRole(change.role)) return `the role ${change.role} isn't in the project`;
            return validNumber(change.cost, MODEL_CHANGE_LIMITS.cost) ? undefined : 'it has no valid cost';
        case 'systemCost':
            if (!hasSystem(change.system)) return `the system ${change.system} isn't in the project`;
            return validNumber(change.cost, MODEL_CHANGE_LIMITS.cost) ? undefined : 'it has no valid cost';
        case 'replaceSystem':
            if (!hasSystem(change.system)) return `the system ${change.system} isn't in the project`;
            if (!change.newSystem?.trim() || sameName(change.system, change.newSystem)) return 'it names no other system to replace it';
            return undefined;
        case 'replaceRole':
            if (!hasRole(change.role)) return `the role ${change.role} isn't in the project`;
            if (!change.newRole?.trim() || sameName(change.role, change.newRole)) return 'it names no other role to replace it';
            if (change.people !== null && !validNumber(change.people, MODEL_CHANGE_LIMITS.people, true)) return 'it has no valid number of people';
            return undefined;
    }
    if (!(MODEL_CHANGE_KINDS as readonly string[]).includes(change.kind)) return 'it is not a change LINQ can make';

    // a new step: in one of the project's models, after a step or information found when it's made, done by someone
    if (change.kind === 'add') {
        if (!change.action?.trim()) return 'it names no new step';
        if (!(context.models || []).some((m) => sameName(m, change.model))) return `the model ${change.model} isn't in the project`;
        if (!change.from?.trim()) return 'it names no step for the new one to follow';
        if (!change.newRole?.trim() && !change.system?.trim()) return 'it names no role or system to do the new step';
        if (change.minutes !== null && change.minutes !== undefined && !validNumber(change.minutes, MODEL_CHANGE_LIMITS.minutes)) return 'it has no valid time';
        if (change.people !== null && change.people !== undefined && !validNumber(change.people, MODEL_CHANGE_LIMITS.people, true)) return 'it has no valid number of people';
        return undefined;
    }

    // the rest change an action
    if (!(context.actions || []).some((a) => sameName(a.name, change.action) && (!change.model || sameName(a.model, change.model)))) return `the action ${change.action} isn't in the project`;
    if (change.kind === 'share') {
        if (!change.from?.trim()) return 'it names nowhere the items leave from';
        return validNumber(change.percent, 100) ? undefined : 'it has no valid share of items';
    }
    if (change.kind === 'volume') return validNumber(change.items, MODEL_CHANGE_LIMITS.items) ? undefined : 'it has no valid number of items';
    if ((change.kind === 'automate' || change.kind === 'assist') && !change.system?.trim()) return 'it names no system';
    if (change.kind === 'reassign') {
        if (!change.newRole?.trim()) return 'it names no role to take the action on';
        if (change.role && !hasRole(change.role)) return `the role ${change.role} isn't in the project`;
        if (change.people !== null && !validNumber(change.people, MODEL_CHANGE_LIMITS.people, true)) return 'it has no valid number of people';
    }
    if ((change.kind === 'retime' || change.percent !== null) && !validNumber(change.percent, MODEL_CHANGE_LIMITS.percent)) return 'it has no valid time';
    return undefined;
};
